import PropTypes from 'prop-types';
import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FieldArray } from 'formik';
import { NumericFormat } from 'react-number-format';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// material
import {
  Stack,
  TextField,
  Box,
  Card,
  CardHeader,
  IconButton,
  Fab,
  Grid,
  InputAdornment,
  Divider,
  Chip,
  Tab
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
// material
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import trashFill from '@iconify/icons-eva/trash-2-fill';

dayjs.extend(utc);
dayjs.extend(timezone);

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  border: `1px dashed ${grey[300]}`
}));

TicketFormBatch.propTypes = {
  formik: PropTypes.object.isRequired
};

export default function TicketFormBatch({ formik }) {
  const { values, errors, touched, getFieldProps, setFieldValue } = formik;

  return (
    <FieldArray
      name="rules"
      render={(fieldArrayHelpers) => (
        <>
          {values.rules.map((rule, i) => (
            <CardStyle key={i}>
              <CardHeader
                sx={{ px: { xs: 2, sm: 3 } }}
                title={`Rule ${i + 1}`}
                action={
                  i > 0 && (
                    <IconButton
                      color="error"
                      aria-label="delete"
                      onClick={() => fieldArrayHelpers.remove(i)}
                    >
                      <Icon icon={trashFill} />
                    </IconButton>
                  )
                }
              />
              <Stack spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
                <TextField
                  fullWidth
                  label="[EN] Name*"
                  placeholder={`i.e. Batch ${i + 1}`}
                  {...getFieldProps(`rules.${i}.name`)}
                  error={Boolean(
                    touched.rules && touched.rules[i]?.name && errors.rules && errors.rules[i]?.name
                  )}
                  helperText={
                    touched.rules && touched.rules[i]?.name && errors.rules && errors.rules[i]?.name
                  }
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expires In*"
                    value={rule.tmpExpiresIn || null}
                    name="expiresIn"
                    showToolbar={false}
                    onChange={(date) => {
                      setFieldValue(`rules.${i}.tmpExpiresIn`, date);
                      // PS: Aqui não é para coloca timezone Lisbon
                      // Pois pega o date/timezone atual e depois converte
                      const expiresIn = new Date(date);
                      expiresIn.setHours(23, 59, 59, 999);
                      setFieldValue(
                        `rules.${i}.expiresIn`,
                        dayjs(expiresIn).tz('Europe/Lisbon', true)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...getFieldProps(`rules.${i}.expiresIn`)}
                        helperText={
                          (touched.rules &&
                            touched.rules[i]?.expiresIn &&
                            errors.rules &&
                            errors.rules[i]?.expiresIn) ||
                          params?.inputProps?.placeholder
                        }
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>

                <Grid container>
                  <Grid item xs={6} pr={1}>
                    <NumericFormat
                      fullWidth
                      label="Discount (0 - 100%)*"
                      {...getFieldProps(`rules.${i}.discount`)}
                      error={Boolean(
                        touched.rules &&
                          touched.rules[i]?.discount &&
                          errors.rules &&
                          errors.rules[i]?.discount
                      )}
                      helperText={
                        touched.rules &&
                        touched.rules[i]?.discount &&
                        errors.rules &&
                        errors.rules[i]?.discount
                      }
                      decimalScale={0}
                      customInput={TextField}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} pl={1}>
                    <NumericFormat
                      fullWidth
                      label="Limit*"
                      {...getFieldProps(`rules.${i}.limit`)}
                      error={Boolean(
                        touched.rules &&
                          touched.rules[i]?.limit &&
                          errors.rules &&
                          errors.rules[i]?.limit
                      )}
                      helperText={
                        touched.rules &&
                        touched.rules[i]?.limit &&
                        errors.rules &&
                        errors.rules[i]?.limit
                      }
                      decimalScale={0}
                      customInput={TextField}
                    />
                  </Grid>
                </Grid>

                <Divider>
                  <Chip label="Translations" size="small" />
                </Divider>

                <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
                  <TabContext value="pt">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <TabList>
                        <Tab label="PT" value="pt" />
                      </TabList>
                    </Box>
                    <TabPanel value="pt">
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="[PT] Name"
                          variant="filled"
                          {...getFieldProps(`rules.${i}.translation.pt.name`)}
                          error={Boolean(
                            touched.rules &&
                              touched.rules[i]?.translation?.pt?.name &&
                              errors.rules &&
                              errors.rules[i]?.translation?.pt?.name
                          )}
                          helperText={
                            touched.rules &&
                            touched.rules[i]?.translation?.pt?.name &&
                            errors.rules &&
                            errors.rules[i]?.translation?.pt?.name
                          }
                        />
                      </Stack>
                    </TabPanel>
                  </TabContext>
                </Box>
              </Stack>
            </CardStyle>
          ))}

          <Box sx={{ textAlign: 'center', mt: -3 }}>
            <Fab
              size="medium"
              color="primary"
              aria-label="add"
              onClick={() =>
                fieldArrayHelpers.push({
                  name: '',
                  expiresIn: null,
                  tmpExpiresIn: null,
                  discount: '',
                  limit: ''
                })
              }
            >
              <Icon icon={plusFill} width="24" />
            </Fab>
          </Box>
        </>
      )}
    />
  );
}
